import React from "react";
import { Checkbox, File, Input } from "@peracto/peracto-ui";
import Select from "react-select";
import BrandRestrictor from "./AccountsList/components/brandRestrictor";
import BrandSorter from "./AccountsList/components/brandSorter";

const Option = ({ data, innerProps }) => (
    <div {...innerProps}>{data.displayComponent}</div>
);

const SingleValue = ({ data }) => <div>{data.displayComponent}</div>;

const BabbageAccountConfigurationFields = ({
    // Field state
    isKeyAccount, setIsKeyAccount,
    showStrippedFooter, setShowStrippedFooter,
    restrictHaynesProAccess, setRestrictHaynesProAccess,
    hideOmnihub, setHideOmnihub,
    showOffers, setShowOffers,
    vehicleAgePopup, setVehicleAgePopup,
    excludeMenuItems, setExcludeMenuItems,
    themeColours, setThemeColours,
    categoryBrandRestrictions, setCategoryBrandRestrictions,
    categoryBrandPriorities, setCategoryBrandPriorities,
    selectedCategory, setSelectedCategory,
    restrictedBrands, setRestrictedBrands,
    prioritisedBrands, setPrioritisedBrands,
    // Supporting data
    categoryOptions, categoriesLoading,
    brands, brandsLoading,
    t3CategoryOptions, t3CategoriesLoading,
    // Helpers
    resetColours,
    // Logo preview
    customLogo,
}) => {
    return (
        <>
            <div className="mb-10">
                <div className="mb-2">
                    <p className="mb-1 text-[13.6px] text-brand-grey-700">
                        Category Settings
                    </p>
                    <p className="text-[12.8px] text-[#aaaaaa] mb-2 font-[300]">
                        Select a category to hide or sort brands
                    </p>

                    <Select
                        options={t3CategoryOptions}
                        name="t3Categories"
                        testId="t3_categories"
                        classNamePrefix="select"
                        placeholder="Select a category"
                        value={selectedCategory}
                        components={{ Option, SingleValue }}
                        isSearchable
                        className="basic-single"
                        onChange={(e) => {
                            setRestrictedBrands([]);
                            setPrioritisedBrands([]);
                            setSelectedCategory(e || null);
                            const selectedCategoryId = e?.value;

                            setRestrictedBrands(
                                categoryBrandRestrictions
                                    ?.filter(
                                        (restriction) =>
                                            restriction.category === selectedCategoryId
                                    )
                                    ?.map((restriction) => ({
                                        ...restriction,
                                        label: brands?.find(
                                            (brand) => restriction?.brand === brand?.value
                                        )?.label,
                                        category: selectedCategoryId,
                                        value: restriction?.brand,
                                    }))
                            );
                            setPrioritisedBrands(
                                categoryBrandPriorities
                                    ?.filter(
                                        (priority) =>
                                            priority.category === selectedCategoryId
                                    )
                                    ?.map((priority) => ({
                                        ...priority,
                                        id: crypto.randomUUID(),
                                    }))
                                    .sort(
                                        (a, b) => (a?.priority ?? 0) - (b?.priority ?? 0)
                                    )
                            );
                        }}
                        isLoading={t3CategoriesLoading}
                        isDisabled={t3CategoriesLoading}
                    />
                </div>

                <BrandRestrictor
                    brands={brands}
                    restrictedBrands={restrictedBrands}
                    setRestrictedBrands={setRestrictedBrands}
                    brandsLoading={brandsLoading}
                    selectedCategory={selectedCategory}
                    categoryBrandRestrictions={categoryBrandRestrictions}
                    setCategoryBrandRestrictions={setCategoryBrandRestrictions}
                />
                <BrandSorter
                    brandOptions={brands}
                    brandsLoading={brandsLoading}
                    selectedCategory={selectedCategory}
                    categoryBrandPriorities={categoryBrandPriorities}
                    setCategoryBrandPriorities={setCategoryBrandPriorities}
                    prioritisedBrands={prioritisedBrands}
                    setPrioritisedBrands={setPrioritisedBrands}
                />
            </div>

            <Checkbox
                name="restrictHaynesProAccess"
                label="Restrict Haynes Pro Access?"
                testId="restrict_haynes_pro_access"
                value={restrictHaynesProAccess}
                onChange={(e) => setRestrictHaynesProAccess(e.target.checked)}
            />
            <Checkbox
                name="hideOmnihub"
                label="Hide Omnihub?"
                testId="hide_omnihub"
                value={hideOmnihub}
                onChange={(e) => setHideOmnihub(e.target.checked)}
            />
            <Checkbox
                name="showOffers"
                label="Show Offers?"
                testId="show_offers"
                value={showOffers}
                onChange={(e) => setShowOffers(e.target.checked)}
            />
            <Checkbox
                name="vehicleAgePopup"
                label="Enable Vehicle Age Popup?"
                testId="vehicle_age_popup"
                value={vehicleAgePopup}
                onChange={(e) => setVehicleAgePopup(e.target.checked)}
            />

            <div className="mb-4">
                <label
                    className="block mb-1 font-medium"
                    htmlFor="excludeMenuItems"
                >
                    Excluded Menu Items
                </label>
                <Select
                    id="excludeMenuItems"
                    name="excludeMenuItems"
                    isMulti
                    options={categoryOptions}
                    value={excludeMenuItems}
                    onChange={setExcludeMenuItems}
                    classNamePrefix="react-select"
                    placeholder="Select menu items to exclude..."
                    isDisabled={categoriesLoading}
                />
                <small className="text-gray-500">
                    Prevents the selected menu items from displaying for the Babbage account
                </small>
            </div>

            <Checkbox
                name="isKeyAccount"
                label="Allow colour and logo customisation?"
                testId="is_key_account"
                value={isKeyAccount}
                onChange={(e) => {
                    setIsKeyAccount(e.target.checked);
                    if (!e.target.checked) {
                        setShowStrippedFooter(false);
                    }
                }}
            />
            <div style={{ opacity: isKeyAccount ? 1 : 0.5 }}>
                <Checkbox
                    name="showStrippedFooter"
                    label="Display stripped-back Omnipart footer?"
                    testId="show_stripped_footer"
                    help="Removes the LKQ logo, Euro Car Parts logo and award images from the footer"
                    disabled={!isKeyAccount}
                    value={showStrippedFooter}
                    onChange={(e) => setShowStrippedFooter(e.target.checked)}
                />
            </div>

            <div className={`${!isKeyAccount ? "opacity-50" : ""}`}>
                <File
                    name="customLogo"
                    label="Custom Logo"
                    disabled={!isKeyAccount}
                    help="Custom logo for the site header. Recommended: width: 200px, height: 60px, file size up to 50kb. Accepted formats: .png, .jpg, .jpeg, .svg"
                />
                {customLogo && (
                    <div className="mt-2 mb-4">
                        <img
                            src={customLogo}
                            alt="Custom Logo Preview"
                            className="max-h-[100px] object-contain"
                        />
                    </div>
                )}
            </div>

            <div className={`w-full md:w-1/3 ${!isKeyAccount ? "opacity-50" : ""}`}>
                <Input
                    name="primaryColour"
                    type="color"
                    label="Primary Theme Colour"
                    help="Headings, coloured text and icons, background areas and buttons"
                    disabled={!isKeyAccount}
                    value={themeColours?.primaryColour ?? ""}
                    onChange={(e) =>
                        setThemeColours({ ...themeColours, primaryColour: e.target.value })
                    }
                />
            </div>

            <div className={`w-full md:w-1/3 ${!isKeyAccount ? "opacity-50" : ""}`}>
                <Input
                    name="secondaryColour"
                    type="color"
                    label="Secondary Theme Colour"
                    help="Success buttons and accent colours"
                    disabled={!isKeyAccount}
                    value={themeColours?.secondaryColour ?? ""}
                    onChange={(e) =>
                        setThemeColours({ ...themeColours, secondaryColour: e.target.value })
                    }
                />
            </div>

            <div className={`w-full md:w-1/3 ${!isKeyAccount ? "opacity-50" : ""}`}>
                <Input
                    name="tertiaryColour"
                    type="color"
                    label="Tertiary Theme Colour"
                    help="Utility bar and accent colours"
                    disabled={!isKeyAccount}
                    value={themeColours?.tertiaryColour ?? ""}
                    onChange={(e) =>
                        setThemeColours({ ...themeColours, tertiaryColour: e.target.value })
                    }
                />
            </div>

            <div className="flex flex-col w-fit gap-y-8">
                <div>
                    <button
                        className="btn btn-primary"
                        data-testid="reset-colours"
                        onClick={resetColours}
                        type="button"
                        disabled={!isKeyAccount}
                    >
                        Reset Colours to Default
                    </button>
                    <p
                        className={`mt-2 text-[12.8px] font-[300] text-[#AAAAAA] ${
                            !isKeyAccount ? "opacity-50" : ""
                        }`}
                    >
                        This will reset the colours to the default Omnipart colours.
                    </p>
                </div>
            </div>
        </>
    );
};

export default BabbageAccountConfigurationFields;
